import React, { useCallback, useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Row,
  Table,
  Badge,
  Button,
  Spinner,
  Alert,
} from "reactstrap"
import { setBreadcrumbItems } from "store/actions"
import { connect } from "react-redux"
import apiClient from "helpers/apiClient"

const DASHBOARD_URL = "/api/dashboard"

interface UserStat {
  userId: string
  fullName: string
  email: string
  role?: string
  listingCount?: number
  bidCount?: number
  leadingCount?: number
}

interface BreadcrumbItem {
  title: string
  link: string
}

interface UserStatsProps {
  setBreadcrumbItems: (title: string, items: BreadcrumbItem[]) => void
}

type StatsType = "broker-listings" | "seller-listings" | "buyer-bids" | "admin-bids" | "broker-bids" | "seller-bids"

const typeConfig: Record<StatsType, { title: string; icon: string; color: string; isListings: boolean }> = {
  "broker-listings": { title: "Brokers – Listings", icon: "mdi-account-tie", color: "primary", isListings: true },
  "seller-listings": { title: "Sellers – Listings", icon: "mdi-account-cash", color: "success", isListings: true },
  "buyer-bids": { title: "Buyers – Bids", icon: "mdi-account", color: "info", isListings: false },
  "admin-bids": { title: "Admin – Bids", icon: "mdi-shield-account", color: "danger", isListings: false },
  "broker-bids": { title: "Brokers – Bids", icon: "mdi-account-tie", color: "primary", isListings: false },
  "seller-bids": { title: "Sellers – Bids", icon: "mdi-account-cash", color: "success", isListings: false },
}

const UserStats = (props: UserStatsProps) => {
  const [searchParams] = useSearchParams()
  const type = (searchParams.get("type") || "buyer-bids") as StatsType
  const config = typeConfig[type] || typeConfig["buyer-bids"]

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<UserStat[]>([])

  useEffect(() => {
    props.setBreadcrumbItems(config.title, [
      { title: "Dashboard", link: "/dashboard" },
      { title: config.title, link: "#" },
    ])
  }, [props, config.title])

  const fetchData = useCallback(() => {
    setLoading(true)
    setError(null)
    apiClient
      .get<any>(DASHBOARD_URL)
      .then((res) => {
        const d = res.data?.data || res.data
        let stats: UserStat[] = []
        
        switch (type) {
          case "broker-listings":
            stats = d.brokerStats || []
            break
          case "seller-listings":
            stats = d.sellerStats || []
            break
          case "buyer-bids":
            stats = d.buyerStats || []
            break
          case "admin-bids":
            stats = d.adminBidStats || []
            break
          case "broker-bids":
            stats = d.brokerBidStats || []
            break
          case "seller-bids":
            stats = d.sellerBidStats || []
            break
        }
        setData(stats)
      })
      .catch((e: any) => {
        setError(e?.response?.data?.message || e?.message || "Failed to load data.")
        setData([])
      })
      .finally(() => setLoading(false))
  }, [type])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  if (loading) {
    return (
      <Row>
        <Col className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2">Loading...</p>
        </Col>
      </Row>
    )
  }

  if (error) {
    return (
      <Row>
        <Col>
          <Alert color="danger">{error}</Alert>
          <Button color="primary" onClick={fetchData}>Retry</Button>
        </Col>
      </Row>
    )
  }

  return (
    <div className="user-stats-page">
      <Row>
        <Col xl={12}>
          <Card className="mb-4">
            <CardHeader className="d-flex justify-content-between align-items-center">
              <CardTitle tag="h5" className="mb-0">
                <i className={`mdi ${config.icon} me-2 text-${config.color}`} />
                {config.title}
              </CardTitle>
              <div className="d-flex gap-2">
                <Link to="/dashboard" className="btn btn-sm btn-outline-secondary">
                  <i className="mdi mdi-arrow-left me-1" />
                  Back to Dashboard
                </Link>
                <Button color="primary" size="sm" onClick={fetchData}>
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              <p className="text-muted mb-3">
                {config.isListings
                  ? `All ${type.split("-")[0]}s with their property listings`
                  : `All ${type.split("-")[0]}s with their bid statistics`}
              </p>
              {data.length === 0 ? (
                <Alert color="info">No data available.</Alert>
              ) : (
                <div className="table-responsive">
                  <Table bordered hover className="mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        {config.isListings ? (
                          <th className="text-end">Listings</th>
                        ) : (
                          <>
                            <th className="text-end">Bids</th>
                            <th className="text-end">Leading</th>
                          </>
                        )}
                        <th style={{ width: 100 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((item, idx) => (
                        <tr key={item.userId}>
                          <td>{idx + 1}</td>
                          <td><strong>{item.fullName || "—"}</strong></td>
                          <td><span className="text-muted">{item.email || "—"}</span></td>
                          {config.isListings ? (
                            <td className="text-end">
                              <Badge color={config.color}>{item.listingCount || 0}</Badge>
                            </td>
                          ) : (
                            <>
                              <td className="text-end">
                                <Badge color={config.color}>{item.bidCount || 0}</Badge>
                              </td>
                              <td className="text-end">
                                <Badge color="warning">{item.leadingCount || 0}</Badge>
                              </td>
                            </>
                          )}
                          <td>
                            {config.isListings ? (
                              <Link
                                to={`/listings?createdBy=${item.userId}`}
                                className={`btn btn-sm btn-outline-${config.color}`}
                                title="View"
                              >
                                <i className="mdi mdi-eye" />
                              </Link>
                            ) : (
                              <Link
                                to={`/bidding-overview?userId=${item.userId}`}
                                className={`btn btn-sm btn-outline-${config.color}`}
                                title="View"
                              >
                                <i className="mdi mdi-eye" />
                              </Link>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
              <div className="mt-3 text-muted small">
                Total: {data.length} {type.split("-")[0]}s
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

const mapDispatchToProps = { setBreadcrumbItems }
export default connect(null, mapDispatchToProps)(UserStats)
